import { computed, type IComputedValue } from 'mobx';
import { PAGE_SIZE_IN_DAYS, SLOT_SIZE_IN_MINUTES } from '../constants';
import { MILLISECONDS_IN_DAY, MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import type { OwaDate } from 'owa-datetime';
import { utcDate, getTimestamp, addMilliseconds } from 'owa-datetime';
import type { DateRange } from 'owa-datetime-utils';

const epoch = computed(() => utcDate(1970, 0, 1));
const epochTimestamp = computed(() => getTimestamp(epoch.get()));
const pageSizeInMilliseconds = computed(() => PAGE_SIZE_IN_DAYS * MILLISECONDS_IN_DAY);

export const slotsPerPage: IComputedValue<number> = computed(
    () => pageSizeInMilliseconds.get() / (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES)
);

/**
 * Gets the page number at which the specified time should be found.
 * @param time: The time to perform the lookup against
 */
export function getPageIndex(time: OwaDate): number {
    const timeSinceEpoch = getTimestamp(time) - epochTimestamp.get();
    return Math.floor(timeSinceEpoch / pageSizeInMilliseconds.get());
}

/**
 * Gets the row number in a page in which the specified time should be found.
 * @param time: The time to perform the lookup against
 */
export function getRecordIndex(time: OwaDate): number {
    const timeSinceEpoch = getTimestamp(time) - epochTimestamp.get();
    return Math.floor(
        (timeSinceEpoch % pageSizeInMilliseconds.get()) /
            (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES)
    );
}

export function getPageRange(pageIndex: number): DateRange {
    const start = addMilliseconds(epoch.get(), pageSizeInMilliseconds.get() * pageIndex);
    const end = addMilliseconds(start, pageSizeInMilliseconds.get());

    return { start, end };
}
